import api from './api';
import { AccountSummary, Invoice, InvoiceLine, Payment, InvoiceFilters, DashboardData } from '../types';

interface InvoicesResponse {
  invoices: Invoice[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface PaymentsResponse {
  payments: Payment[];
  pagination: {
    page: number;
    limit: number;
  };
}

interface DashboardResponse {
  success: boolean;
  data: DashboardData;
  warning?: string;
}

interface DashboardFilters {
  startDate?: string;
  endDate?: string;
}

class StatementService {
  // Obtener dashboard completo (nuevo endpoint con datos de Odoo)
  // Acepta filtros de fecha para calcular totales del periodo
  async getDashboard(filters: DashboardFilters = {}): Promise<{ data: DashboardData; warning?: string }> {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const url = params.toString() ? `/statements/dashboard?${params.toString()}` : '/statements/dashboard';
    const response = await api.get<DashboardResponse>(url);
    return {
      data: response.data.data,
      warning: response.data.warning
    };
  }

  // Obtener resumen de cuenta (endpoint original)
  async getAccountSummary(): Promise<AccountSummary> {
    const response = await api.get<{ success: boolean; data: AccountSummary; warning?: string }>(
      '/statements/summary'
    );
    return response.data.data;
  }

  // Obtener facturas
  async getInvoices(filters: InvoiceFilters = {}): Promise<InvoicesResponse> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.status) params.append('status', filters.status);

    const response = await api.get<{ success: boolean; data: InvoicesResponse }>(
      `/statements/invoices?${params.toString()}`
    );
    return response.data.data;
  }

  // Obtener detalle de factura
  async getInvoiceDetail(invoiceId: number): Promise<{ lines: InvoiceLine[] }> {
    const response = await api.get<{ success: boolean; data: { lines: InvoiceLine[] } }>(
      `/statements/invoices/${invoiceId}`
    );
    return response.data.data;
  }

  // Obtener pagos
  async getPayments(page: number = 1, limit: number = 10): Promise<PaymentsResponse> {
    const response = await api.get<{ success: boolean; data: PaymentsResponse }>(
      `/statements/payments?page=${page}&limit=${limit}`
    );
    return response.data.data;
  }

  // Descargar PDF
  async downloadPDF(startDate?: string, endDate?: string): Promise<Blob> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/statements/pdf?${params.toString()}`, {
      responseType: 'blob'
    });

    return response.data;
  }

  // Descargar PDF y abrir en nueva pestaña o descargar
  async generateAndDownloadPDF(startDate?: string, endDate?: string): Promise<void> {
    try {
      const blob = await this.downloadPDF(startDate, endDate);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `estado_cuenta_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando PDF:', error);
      throw error;
    }
  }

  // Enviar estado de cuenta por email
  async sendByEmail(options: {
    email?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ success: boolean; message: string; sentTo: string }> {
    const response = await api.post<{
      success: boolean;
      message: string;
      data: { messageId: string; sentTo: string };
    }>('/statements/send-email', options);

    return {
      success: response.data.success,
      message: response.data.message,
      sentTo: response.data.data.sentTo
    };
  }
}

const statementService = new StatementService();
export default statementService;
